// HTTP-клиент с cookie-based auth.
// JWT хранится в httpOnly cookie (недоступен JS).
// CSRF-токен хранится в обычной cookie, читается и отправляется в заголовке.

/** Имя cookie для CSRF-токена (синхронизировано с backend). */
const CSRF_COOKIE_NAME = '__Host-csrf';

/** Имя заголовка для CSRF-токена. */
const CSRF_HEADER_NAME = 'x-csrf-token';

export interface ApiError extends Error {
  status: number;
  code?: string;
  /** Доп. поля из тела ответа (например, `maxLength` у `prompt_too_long`). */
  details?: Record<string, unknown>;
}

function buildError(status: number, body: unknown): ApiError {
  const err = new Error(`HTTP ${status}`) as ApiError;
  err.status = status;
  if (body && typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    if ('error' in obj) {
      err.code = String(obj.error);
      err.message = err.code;
    }
    // Сохраняем всё тело без поля `error` — там лежат `maxLength`, `path` и т.п.
    const { error: _e, ...rest } = obj;
    if (Object.keys(rest).length > 0) err.details = rest;
  }
  return err;
}

/**
 * Преобразует `ApiError` в человекочитаемое русское сообщение для UI.
 * Используется в `setError(...)` и `toast.error(...)` — чтобы не дублировать
 * маппинг кодов в каждом экране.
 */
export function formatApiError(err: unknown): string {
  if (!err || typeof err !== 'object') return 'Сетевая ошибка';
  const e = err as ApiError;
  switch (e.code) {
    case 'machine_offline':
      return 'Машина офлайн — запусти демон';
    case 'prompt_too_long': {
      const max = typeof e.details?.maxLength === 'number' ? e.details.maxLength : 50_000;
      return `Промпт слишком длинный (максимум ${max.toLocaleString('ru-RU')} символов)`;
    }
    case 'prompt_required':
      return 'Промпт не может быть пустым';
    case 'session_finished':
      return 'Сессия уже завершена';
    case 'not_resumable':
      return 'Эту сессию нельзя возобновить';
    case 'validation_error':
      return typeof e.details?.path === 'string' && e.details.path
        ? `Ошибка валидации: ${e.details.path}`
        : 'Ошибка валидации запроса';
    case undefined:
      return e.message || 'Сетевая ошибка';
    default:
      return e.code;
  }
}

/**
 * Читает CSRF-токен из cookie.
 * Если cookie нет — пользователь не авторизован.
 */
export function getCsrfToken(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CSRF_COOKIE_NAME}=([^;]+)`));
  return match?.[1] ?? null;
}

/**
 * Проверяет, авторизован ли пользователь (по наличию CSRF cookie).
 * JWT в httpOnly cookie недоступен JS, но CSRF-токен есть.
 */
export function isAuthenticated(): boolean {
  return getCsrfToken() !== null;
}

/**
 * Уведомляет приложение о смене auth-состояния.
 * Вызывается после logout или при 401.
 */
export function notifyAuthChange(authenticated: boolean): void {
  window.dispatchEvent(
    new CustomEvent('cr:auth', { detail: { authenticated } }),
  );
}

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
  headers: Record<string, string> = {},
): Promise<T> {
  const h: Record<string, string> = { ...headers };

  // CSRF-токен для state-changing запросов (POST, DELETE, PUT, PATCH).
  // GET запросы не требуют CSRF.
  if (method !== 'GET') {
    const csrf = getCsrfToken();
    if (csrf) h[CSRF_HEADER_NAME] = csrf;
  }

  if (body !== undefined) h['Content-Type'] = 'application/json';

  const res = await fetch(url, {
    method,
    headers: h,
    credentials: 'include', // Отправляем cookies
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    if (res.status === 401) {
      notifyAuthChange(false);
    }
    throw buildError(res.status, payload);
  }
  return payload as T;
}

/** Ответ от POST /api/v1/attachments. */
export interface AttachmentRef {
  id: string;
  mimeType: string;
  byteSize: number;
  sha256: string;
}

/**
 * Загрузить файл через multipart/form-data.
 * Возвращает AttachmentRef для последующей привязки к сессии.
 */
async function uploadFile(file: File | Blob, filename?: string): Promise<AttachmentRef> {
  const url = '/api/v1/attachments';
  const h: Record<string, string> = {};

  // CSRF для file upload
  const csrf = getCsrfToken();
  if (csrf) h[CSRF_HEADER_NAME] = csrf;

  const form = new FormData();
  form.append('file', file, filename ?? (file instanceof File ? file.name : 'image.png'));

  const res = await fetch(url, {
    method: 'POST',
    headers: h,
    credentials: 'include',
    body: form,
  });

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    if (res.status === 401) {
      notifyAuthChange(false);
    }
    throw buildError(res.status, payload);
  }
  return payload as AttachmentRef;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body),
  delete: <T>(url: string) => request<T>('DELETE', url),
  uploadFile,
};

// ─────────────────────────────────────────────────────────────────────────────
// LEGACY: для совместимости с существующим кодом, который проверяет getToken().
// После миграции удалить.
// ─────────────────────────────────────────────────────────────────────────────

const TOKEN_KEY = 'cr_jwt';

/**
 * @deprecated JWT теперь в httpOnly cookie. Используй isAuthenticated().
 * Эта функция проверяет legacy localStorage токен для плавной миграции.
 */
export function getToken(): string | null {
  // Сначала проверяем новый способ (CSRF cookie)
  if (isAuthenticated()) {
    // Возвращаем placeholder — реальный JWT в httpOnly cookie
    return '__cookie_auth__';
  }
  // Fallback на legacy localStorage
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * @deprecated JWT теперь устанавливается через cookie сервером.
 * Эта функция очищает legacy localStorage.
 */
export function setToken(token: string | null): void {
  if (token) {
    // Новая система — игнорируем, JWT приходит через cookie
    if (token === '__cookie_auth__') return;
    // Legacy — сохраняем (для переходного периода)
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    // Logout: чистим localStorage
    localStorage.removeItem(TOKEN_KEY);
  }
  window.dispatchEvent(new CustomEvent('cr:auth', { detail: { token } }));
}
